import { AritoFormV2, BottomActionBarV2, DialogV2, ErpInputTableV2 } from '@/components/custom/erp';
import type { InputTableColumn } from '@/components/custom/erp/input-table-v2';
import { ColumnMapping, ValidationError } from '@/lib/excel';

export interface ExcelImportErrorDialogProps {
  open: boolean;
  onClose: () => void;
  errors: ValidationError[];
  columnMapping: Record<string, ColumnMapping>;
  title?: string;
  errorMessage?: string;
}

const getExcelColumnLetter = (index: number): string => {
  let result = '';
  let currentIndex = index + 1;
  while (currentIndex > 0) {
    currentIndex -= 1;
    result = String.fromCharCode(65 + (currentIndex % 26)) + result;
    currentIndex = Math.floor(currentIndex / 26);
  }
  return result;
};

const normalizeHeaderLabel = (header: string): string => header.replace(/\*/g, '').trim();

const friendlyFieldLabels: Record<string, string> = {
  ma_kh: 'Mã khách hàng',
  ma_vt: 'Mã sản phẩm',
  ma_vt_dvt: 'Đơn vị tính',
  dvt: 'Đơn vị tính',
  ma_kho: 'Mã kho',
  ma_thue: 'Mã thuế'
};

const findColumnMapping = (
  error: ValidationError,
  columnMapping: Record<string, ColumnMapping>
): ColumnMapping | undefined => {
  if (error.columnIndex !== undefined) {
    return Object.values(columnMapping).find(col => col.index === error.columnIndex);
  }

  let mapping = Object.values(columnMapping).find(col => col.key === error.field && col.type === error.section);
  if (!mapping && error.field) {
    mapping = Object.values(columnMapping).find(col => col.key === error.field);
  }
  if (!mapping && error.field) {
    const normalizedField = error.field.toLowerCase();
    mapping = Object.values(columnMapping).find(
      col =>
        normalizeHeaderLabel(col.header).toLowerCase().includes(normalizedField) ||
        normalizedField.includes(normalizeHeaderLabel(col.header).toLowerCase())
    );
  }

  return mapping;
};

const getCellPosition = (error: ValidationError, columnMapping: Record<string, ColumnMapping>): string => {
  if (error.columnIndex !== undefined) {
    return `${getExcelColumnLetter(error.columnIndex)}${error.row}`;
  }

  const mapping = findColumnMapping(error, columnMapping);
  return mapping ? `${getExcelColumnLetter(mapping.index)}${error.row}` : '';
};

const hasRawFieldToken = (message: string): boolean => /(^|\s)[a-z][a-z0-9_]*($|\s)/.test(message);

const getFieldLabel = (error: ValidationError, columnMapping: Record<string, ColumnMapping>): string => {
  const mapping = findColumnMapping(error, columnMapping);
  if (mapping?.header) {
    return normalizeHeaderLabel(mapping.header);
  }

  if (error.field && friendlyFieldLabels[error.field]) {
    return friendlyFieldLabels[error.field];
  }

  if (error.field?.includes('dvt')) {
    return 'Đơn vị tính';
  }
  if (error.field?.includes('ma_vt')) {
    return 'Mã sản phẩm';
  }
  if (error.field?.includes('ma_kh')) {
    return 'Mã khách hàng';
  }

  const messageLabel = error.message?.replace(/\s+không hợp lệ\.?$/i, '').trim();
  if (messageLabel && !hasRawFieldToken(messageLabel)) {
    return messageLabel;
  }

  return 'Dữ liệu';
};

const formatErrorMessage = (
  error: ValidationError,
  columnMapping: Record<string, ColumnMapping>,
  defaultErrorMessage: string
): string => {
  if (!error.message) {
    return defaultErrorMessage;
  }

  const label = getFieldLabel(error, columnMapping);
  if (error.field && error.message.includes(error.field)) {
    return error.message.replace(error.field, label);
  }

  if (hasRawFieldToken(error.message)) {
    return `${label} không hợp lệ`;
  }

  return error.message;
};

export const buildExcelImportErrorRows = (
  errors: ValidationError[],
  columnMapping: Record<string, ColumnMapping>,
  errorMessage: string
) => {
  const groupedErrors = errors.reduce<Record<number, ValidationError[]>>((result, error) => {
    const row = error.row || 0;
    result[row] = [...(result[row] || []), error];
    return result;
  }, {});

  return Object.entries(groupedErrors)
    .sort(([firstRow], [secondRow]) => Number(firstRow) - Number(secondRow))
    .map(([row, rowErrors], index) => {
      const positions = Array.from(
        new Set(rowErrors.map(error => getCellPosition(error, columnMapping)).filter(Boolean))
      );
      const messages = Array.from(
        new Set(rowErrors.map(error => formatErrorMessage(error, columnMapping, errorMessage)).filter(Boolean))
      );

      return {
        id: `${row}-${index}`,
        type: 'Lỗi',
        message: messages.length > 0 ? messages.join('; ') : errorMessage,
        position: positions.length > 0 ? positions.join(', ') : `Dòng ${row}`
      };
    });
};

const columns: InputTableColumn[] = [
  { headerName: 'Loại', field: 'type', width: 80, sortable: false, align: 'center' },
  { headerName: 'Ràng buộc dữ liệu trên chương trình', field: 'message', sortable: false, width: 450 },
  { headerName: 'Vị trí', field: 'position', width: 120, sortable: false }
];

export const ExcelImportErrorDialog = ({
  open,
  onClose,
  errors,
  columnMapping,
  title = 'Chi tiết kết quả Import',
  errorMessage = 'Dữ liệu chưa khai báo trong danh mục hoặc giá trị không hợp lệ.'
}: ExcelImportErrorDialogProps) => {
  const errorRows = buildExcelImportErrorRows(errors, columnMapping, errorMessage);

  return (
    <DialogV2 open={open} onClose={onClose} title={title} maxWidth='lg'>
      <div className='flex flex-col gap-4'>
        <AritoFormV2
          mode='add'
          hasAritoActionBar={false}
          className='h-auto min-w-[725px]'
          headerFields={
            <div className='flex flex-col gap-2'>
              <ErpInputTableV2
                columns={columns}
                rows={errorRows}
                mode='view'
                tableActionButtons={['export']}
                pageSize={100}
              />
            </div>
          }
          bottomBar={<BottomActionBarV2 onClose={onClose} mode='view' />}
        />
      </div>
    </DialogV2>
  );
};
